import type { SummaryAction } from './utils/siteBriefSummaryReview';
import { socketCommand } from './realtime';
declare const BACKEND_URL: string;

import type {
  ChatMessage,
  AvailableToolsListType,
  SystemEnvConfigResponse,
  SystemConnectionHoldTestResponse,
  SystemWebliumAuthCheckResponse,
  ChatCompletionResponse,
  SessionsListResponse,
  CreateSessionResponse,
  CreateSessionOptions,
  GetSessionResponse,
  SessionType,
  PageNameOption,
  SessionSummary,
  SessionRenameOptions,
  WebliumWebsiteMentionItem,
  WebliumWebsitePageMentionItem,
  WebliumWebsiteBlockMentionItem,
  WebliumWebsiteElementMentionItem,
  DecisionsListResponse,
  DecisionActionResponse,
  DecisionListItemType,
  DecisionValueType,
  DecisionUpgradeRequiredStateUpdateType,
  MessageOfferPlanUpgradeStateUpdateType,
  DecisionWebsitePublishStateUpdateType,
  MessageOfferPlanUpgradeActionResponse,
  CustomCodeSectionsType,
  EmbedCodeSectionsType,
  EmbedCodeDataType,
  AdminBlockContentSummaryType,
  AdminBlockContentPushResponseType,
  AdminTextElementType,
  TextElementQuickActionType,
  TextElementQuickActionResponseType,
  SiteBriefListResponseType,
  SiteBriefListScopeType,
  SiteBriefConversationReceipt,
  SiteBriefTaskType,
  SiteBriefChatMetadata,
  SiteBriefTaskResponse,
  SiteBriefRequirementDefinitionType,
  SiteBriefFontCatalogEntryType,
  SiteBriefRegistryOptionsType,
  SiteBriefType,
  SiteBriefRequirementEventType,
  SiteBriefStatusType,
  SiteBriefRequirementInputType,
  RenderedSiteType,
  SiteStructureReadinessType,
  SiteStructureType
} from './api.types';

const normalizeNullableString = (value?: string | PageNameOption | '' | null): string | null => {
  if (typeof value !== 'string') {
    return value ?? null;
  }
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
};

const isNonEmptyString = (value: unknown): value is string => {
  return typeof value === 'string' && value.trim().length > 0;
};

const formatApiErrorDetails = (details: unknown): string | null => {
  if (Array.isArray(details)) {
    const detailMessages = details
      .map((detail) => {
        if (!detail || typeof detail !== 'object') {
          return null;
        }

        const message = (detail as { message?: unknown }).message;
        return isNonEmptyString(message) ? message.trim() : null;
      })
      .filter((message): message is string => message !== null);

    return detailMessages.length > 0 ? detailMessages.join('\n') : null;
  }

  if (isNonEmptyString(details)) {
    return details.trim();
  }

  return null;
};

const formatApiErrorMessage = (errorData: unknown, status: number, fallbackMessage?: string): string => {
  if (!errorData || typeof errorData !== 'object') {
    return fallbackMessage || `API request failed with status ${status}`;
  }

  const { error, message, details } = errorData as {
    error?: unknown;
    message?: unknown;
    details?: unknown;
  };

  const normalizedError = isNonEmptyString(error) ? error.trim() : null;
  const normalizedMessage = isNonEmptyString(message) ? message.trim() : null;
  const normalizedDetails = formatApiErrorDetails(details);

  if (normalizedError && normalizedMessage) {
    return `${normalizedError}: ${normalizedMessage}`;
  }

  if (normalizedError && normalizedDetails) {
    return normalizedDetails.includes('\n')
      ? `${normalizedError}:\n${normalizedDetails}`
      : `${normalizedError}: ${normalizedDetails}`;
  }

  if (normalizedMessage) {
    return normalizedMessage;
  }

  if (normalizedDetails) {
    return normalizedDetails;
  }

  if (normalizedError) {
    return normalizedError;
  }

  return fallbackMessage || `API request failed with status ${status}`;
};

export type OAuthProvider = 'google' | 'google-workspace' | 'apple';

export const getOAuthLoginUrl = (provider: OAuthProvider): string => `${BACKEND_URL}/api/auth/${provider}`;

export const login = async (username: string, password: string): Promise<string> => {
  const response = await fetch(`${BACKEND_URL}/api/auth/login`, {
    method: 'POST',
    headers: {
      'X-Internal-Authorization': `Bearer ${btoa(`${username}:${password}`)}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    throw new Error('Login failed');
  }

  const { authToken } = await response.json();
  return authToken;
};

export const logout = async (token: string): Promise<void> => {
  const response = await fetch(`${BACKEND_URL}/api/auth/logout`, {
    method: 'POST',
    headers: { 'X-Internal-Authorization': `Bearer ${token}` },
    keepalive: true,
  });
  if (!response.ok) {
    throw new Error('Unable to notify websocket sessions about logout');
  }
};

export const verifyToken = async (token: string): Promise<boolean> => {
  const response = await fetch(`${BACKEND_URL}/api/auth/verify-token`, {
    method: 'POST',
    headers: {
      'X-Internal-Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    return false;
  }

  const { valid, username, message } = await response.json();
  return valid;
};

export const sendChatRequest = async (
  messages: ChatMessage[],
  token: string,
  allowedTools: string[] = [],
  sessionId: string
): Promise<ChatCompletionResponse> => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/agent/completion`, {
      method: 'POST',
      headers: {
        'X-Internal-Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        messages,
        allowedTools,
        sessionId
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error("Server return error:", errorData);

      // Handle limit exceeded errors with special formatting
      if (errorData.error === 'LIMIT_EXCEEDED') {
        const limitInfo = errorData.limitType ? ` (${errorData.limitType})` : '';
        const usageInfo = errorData.currentValue !== undefined && errorData.maxValue !== undefined
          ? `\n\n📊 Current usage: ${errorData.currentValue.toLocaleString()} / ${errorData.maxValue.toLocaleString()}`
          : '';
        const limitClassInfo = errorData.limitClass && errorData.limitClass !== 'private'
          ? `\n🏷️ Your limit class: ${errorData.limitClass}`
          : '';
        throw new Error(`⚠️ Limit Exceeded${limitInfo}\n\n${errorData.message}${usageInfo}${limitClassInfo}`);
      }

      throw new Error(formatApiErrorMessage(errorData, response.status));
    }

    const parsedResponse = await response.json();

    parsedResponse.lastReply.suggestions = { type: '', options: [] };
    parsedResponse.decisions = Array.isArray(parsedResponse.decisions) ? parsedResponse.decisions : [];

    return parsedResponse as ChatCompletionResponse;
  } catch (error) {
    const err = error as Error;
    console.error('API request failed:', error);
    return {
      status: 'Error',
      //messages: [],
      lastToolsCalls: [],
      lastReply: {
        id: '',
        role: 'assistant',
        content: err.message || 'An error occurred while processing your request.',
        timestamp: new Date().toISOString(),
        duration: 0,
        is_liked: false,
        is_disliked: false
      },
      mentions: {
        website: {
          id: null,
          name: '',
          status: '',
          domainLabel: ''
        },
        pages: [],
        blocks: [],
        elements: [],
        orders: [],
        products: []
      },
      decisions: [],
      currentContext: {
        websiteId: null,
        websiteName: null,
        pageName: null
      },
      isConversationEnded: false,
      result: {},
      totalTokens: 0
    };
  }
};

export const fetchAvailableTools = async (token: string): Promise<AvailableToolsListType[]> => {
  const response = await fetch(`${BACKEND_URL}/api/agent/tools/available`, {
    method: 'GET',
    headers: {
      'X-Internal-Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    }
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(formatApiErrorMessage(errorData, response.status));
  }

  const parsedResponse = await response.json();
  if (parsedResponse.status !== 'OK') {
    throw new Error(parsedResponse.message || 'Failed to parse response from /api/agent/tools/available');
  }

  return parsedResponse.tools as AvailableToolsListType[];
}

export const runSystemConnectionHoldTest = async (token: string): Promise<SystemConnectionHoldTestResponse> => {
  const response = await fetch(`${BACKEND_URL}/api/system/connection-hold-test`, {
    method: 'POST',
    headers: {
      'X-Internal-Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    }
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(formatApiErrorMessage(errorData, response.status, 'Connection hold test failed'));
  }

  const parsedResponse = await response.json();
  if (parsedResponse.status !== 'OK') {
    throw new Error(parsedResponse.message || 'Failed to parse response from /api/system/connection-hold-test');
  }

  return parsedResponse as SystemConnectionHoldTestResponse;
}

export const fetchSystemEnvConfig = async (token: string): Promise<SystemEnvConfigResponse> => {
  const response = await fetch(`${BACKEND_URL}/api/system/config`, {
    method: 'GET',
    headers: {
      'X-Internal-Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    }
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(formatApiErrorMessage(errorData, response.status, 'Failed to load system config'));
  }

  const parsedResponse = await response.json();
  if (parsedResponse.status !== 'OK') {
    throw new Error(parsedResponse.message || 'Failed to parse response from /api/system/config');
  }

  return parsedResponse as SystemEnvConfigResponse;
}

export const checkSystemWebliumAuth = async (token: string): Promise<SystemWebliumAuthCheckResponse> => {
  const response = await fetch(`${BACKEND_URL}/api/system/weblium-auth-check`, {
    method: 'POST',
    headers: {
      'X-Internal-Authorization': `Bearer ${token}`,
    }
  });

  const parsedResponse = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(formatApiErrorMessage(parsedResponse, response.status, 'Weblium authentication check failed'));
  }

  if (
    parsedResponse.status !== 'OK'
    || parsedResponse.upstreamStatus < 200
    || parsedResponse.upstreamStatus >= 300
  ) {
    throw new Error(parsedResponse.message || 'Failed to parse response from /api/system/weblium-auth-check');
  }

  return parsedResponse as SystemWebliumAuthCheckResponse;
}

export const fetchWebliumWebsites = async (token: string): Promise<WebliumWebsiteMentionItem[]> => {
  const response = await fetch(`${BACKEND_URL}/api/agent/weblium/websites`, {
    method: 'GET',
    headers: {
      'X-Internal-Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    }
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(formatApiErrorMessage(errorData, response.status));
  }

  const parsedResponse = await response.json();
  if (parsedResponse.status !== 'OK' || !Array.isArray(parsedResponse.websites)) {
    throw new Error(parsedResponse.message || 'Failed to parse response from /api/agent/weblium/websites');
  }

  return parsedResponse.websites as WebliumWebsiteMentionItem[];
}

export const fetchWebliumWebsitePages = async (
  token: string,
  websiteId: string
): Promise<WebliumWebsitePageMentionItem[]> => {
  const response = await fetch(`${BACKEND_URL}/api/agent/weblium/websites/${encodeURIComponent(websiteId)}/pages`, {
    method: 'GET',
    headers: {
      'X-Internal-Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    }
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(formatApiErrorMessage(errorData, response.status));
  }

  const parsedResponse = await response.json();
  if (parsedResponse.status !== 'OK' || !Array.isArray(parsedResponse.pages)) {
    throw new Error(parsedResponse.message || 'Failed to parse response from /api/agent/weblium/websites/:websiteId/pages');
  }

  return parsedResponse.pages as WebliumWebsitePageMentionItem[];
}

export const fetchWebliumWebsiteBlocks = async (
  token: string,
  websiteId: string,
  pageId: string
): Promise<WebliumWebsiteBlockMentionItem[]> => {
  const response = await fetch(
    `${BACKEND_URL}/api/agent/weblium/websites/${encodeURIComponent(websiteId)}/pages/${encodeURIComponent(pageId)}/blocks`,
    {
      method: 'GET',
      headers: {
        'X-Internal-Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      }
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(formatApiErrorMessage(errorData, response.status));
  }

  const parsedResponse = await response.json();
  if (parsedResponse.status !== 'OK' || !Array.isArray(parsedResponse.blocks)) {
    throw new Error(parsedResponse.message || 'Failed to parse response from /api/agent/weblium/websites/:websiteId/pages/:pageId/blocks');
  }

  return parsedResponse.blocks as WebliumWebsiteBlockMentionItem[];
}

export const fetchWebliumWebsiteBlockElements = async (
  token: string,
  websiteId: string,
  pageId: string,
  blockId: string
): Promise<WebliumWebsiteElementMentionItem[]> => {
  const response = await fetch(
    `${BACKEND_URL}/api/agent/weblium/websites/${encodeURIComponent(websiteId)}/pages/${encodeURIComponent(pageId)}/blocks/${encodeURIComponent(blockId)}/elements`,
    {
      method: 'GET',
      headers: {
        'X-Internal-Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      }
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(formatApiErrorMessage(errorData, response.status));
  }

  const parsedResponse = await response.json();
  if (parsedResponse.status !== 'OK' || !Array.isArray(parsedResponse.elements)) {
    throw new Error(parsedResponse.message || 'Failed to parse response from /api/agent/weblium/websites/:websiteId/pages/:pageId/blocks/:blockId/elements');
  }

  return parsedResponse.elements as WebliumWebsiteElementMentionItem[];
}

const EMPTY_CUSTOM_CODE_SECTIONS: CustomCodeSectionsType = {
  head: '',
  afterBody: '',
  beforeBody: '',
  css: ''
};

const EMPTY_EMBED_CODE_SECTIONS: EmbedCodeSectionsType = {
  html: '',
  cssLess: '',
  js: ''
};

const normalizeCustomCodeSections = (value: unknown): CustomCodeSectionsType => {
  if (!value || typeof value !== 'object') {
    return { ...EMPTY_CUSTOM_CODE_SECTIONS };
  }

  const source = value as Partial<Record<keyof CustomCodeSectionsType, unknown>>;

  return {
    head: typeof source.head === 'string' ? source.head : '',
    afterBody: typeof source.afterBody === 'string' ? source.afterBody : '',
    beforeBody: typeof source.beforeBody === 'string' ? source.beforeBody : '',
    css: typeof source.css === 'string' ? source.css : ''
  };
};

const normalizeEmbedCodeSections = (value: unknown): EmbedCodeSectionsType => {
  if (!value || typeof value !== 'object') {
    return { ...EMPTY_EMBED_CODE_SECTIONS };
  }

  const source = value as Partial<Record<keyof EmbedCodeSectionsType, unknown>>;

  return {
    html: typeof source.html === 'string' ? source.html : '',
    cssLess: typeof source.cssLess === 'string' ? source.cssLess : '',
    js: typeof source.js === 'string' ? source.js : ''
  };
};

const normalizeEmbedCodeData = (value: unknown): EmbedCodeDataType => {
  if (!value || typeof value !== 'object') {
    return {
      sections: { ...EMPTY_EMBED_CODE_SECTIONS },
      isolateStyles: false
    };
  }

  const source = value as {
    sections?: unknown;
    isolateStyles?: unknown;
  };

  return {
    sections: normalizeEmbedCodeSections(source.sections),
    isolateStyles: typeof source.isolateStyles === 'boolean' ? source.isolateStyles : false
  };
};

const normalizeAdminBlockContentSummary = (value: unknown): AdminBlockContentSummaryType => {
  if (!value || typeof value !== 'object') {
    return {
      websiteId: '',
      pageId: '',
      blockId: '',
      flatElementsListItemsCount: 0
    };
  }

  const source = value as {
    websiteId?: unknown;
    pageId?: unknown;
    blockId?: unknown;
    flatElementsListItemsCount?: unknown;
  };

  const normalizedCount = typeof source.flatElementsListItemsCount === 'number'
    && Number.isFinite(source.flatElementsListItemsCount)
    ? source.flatElementsListItemsCount
    : 0;

  return {
    websiteId: typeof source.websiteId === 'string' ? source.websiteId : '',
    pageId: typeof source.pageId === 'string' ? source.pageId : '',
    blockId: typeof source.blockId === 'string' ? source.blockId : '',
    flatElementsListItemsCount: normalizedCount
  };
};

const fetchAdminBlockContentDataByBlockId = async (
  token: string,
  websiteId: string,
  pageId: string,
  blockId: string,
  routeSuffix: string,
  responseField: string,
  fallbackMessage: string
): Promise<unknown> => {
  const response = await fetch(
    `${BACKEND_URL}/api/agent/admin/block-content/${encodeURIComponent(websiteId)}/pages/${encodeURIComponent(pageId)}/blocks/${encodeURIComponent(blockId)}${routeSuffix}`,
    {
      method: 'GET',
      headers: {
        'X-Internal-Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      }
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(formatApiErrorMessage(errorData, response.status, fallbackMessage));
  }

  const parsedResponse = await response.json();
  if (parsedResponse.status !== 'OK') {
    throw new Error(parsedResponse.message || fallbackMessage);
  }

  return parsedResponse.blockContent?.[responseField];
};

export const fetchAdminCustomCodeByWebsiteId = async (
  token: string,
  websiteId: string
): Promise<CustomCodeSectionsType> => {
  const response = await fetch(`${BACKEND_URL}/api/agent/admin/custom-code/${encodeURIComponent(websiteId)}`, {
    method: 'GET',
    headers: {
      'X-Internal-Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    }
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(formatApiErrorMessage(errorData, response.status, 'Failed to load custom code'));
  }

  const parsedResponse = await response.json();
  if (parsedResponse.status !== 'OK') {
    throw new Error(parsedResponse.message || 'Failed to parse response from /api/agent/admin/custom-code/:websiteId');
  }

  return normalizeCustomCodeSections(parsedResponse.customCode?.sections);
};

export const saveAdminCustomCodeByWebsiteId = async (
  token: string,
  websiteId: string,
  sections: CustomCodeSectionsType
): Promise<CustomCodeSectionsType> => {
  const response = await fetch(`${BACKEND_URL}/api/agent/admin/custom-code/${encodeURIComponent(websiteId)}`, {
    method: 'POST',
    headers: {
      'X-Internal-Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(sections)
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(formatApiErrorMessage(errorData, response.status, 'Failed to save custom code'));
  }

  const parsedResponse = await response.json();
  if (parsedResponse.status !== 'OK') {
    throw new Error(parsedResponse.message || 'Failed to parse response from /api/agent/admin/custom-code/:websiteId');
  }

  return normalizeCustomCodeSections(parsedResponse.customCode?.sections);
};

export const fetchAdminEmbedCodeByBlockId = async (
  token: string,
  websiteId: string,
  pageId: string,
  blockId: string
): Promise<EmbedCodeDataType> => {
  const response = await fetch(
    `${BACKEND_URL}/api/agent/admin/embed-code/${encodeURIComponent(websiteId)}/pages/${encodeURIComponent(pageId)}/blocks/${encodeURIComponent(blockId)}`,
    {
      method: 'GET',
      headers: {
        'X-Internal-Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      }
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(formatApiErrorMessage(errorData, response.status, 'Failed to load embed code'));
  }

  const parsedResponse = await response.json();
  if (parsedResponse.status !== 'OK') {
    throw new Error(parsedResponse.message || 'Failed to parse response from /api/agent/admin/embed-code/:websiteId/pages/:pageId/blocks/:blockId');
  }

  return normalizeEmbedCodeData(parsedResponse.embedCode);
};

export const saveAdminEmbedCodeByBlockId = async (
  token: string,
  websiteId: string,
  pageId: string,
  blockId: string,
  embedCode: EmbedCodeDataType
): Promise<EmbedCodeDataType> => {
  const response = await fetch(
    `${BACKEND_URL}/api/agent/admin/embed-code/${encodeURIComponent(websiteId)}/pages/${encodeURIComponent(pageId)}/blocks/${encodeURIComponent(blockId)}`,
    {
      method: 'POST',
      headers: {
        'X-Internal-Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...embedCode.sections,
        isolateStyles: embedCode.isolateStyles
      })
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(formatApiErrorMessage(errorData, response.status, 'Failed to save embed code'));
  }

  const parsedResponse = await response.json();
  if (parsedResponse.status !== 'OK') {
    throw new Error(parsedResponse.message || 'Failed to parse response from /api/agent/admin/embed-code/:websiteId/pages/:pageId/blocks/:blockId');
  }

  return normalizeEmbedCodeData(parsedResponse.embedCode);
};

export const fetchAdminBlockContentSummaryByBlockId = async (
  token: string,
  websiteId: string,
  pageId: string,
  blockId: string
): Promise<AdminBlockContentSummaryType> => {
  const response = await fetch(
    `${BACKEND_URL}/api/agent/admin/block-content/${encodeURIComponent(websiteId)}/pages/${encodeURIComponent(pageId)}/blocks/${encodeURIComponent(blockId)}`,
    {
      method: 'GET',
      headers: {
        'X-Internal-Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      }
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(formatApiErrorMessage(errorData, response.status, 'Failed to load block content summary'));
  }

  const parsedResponse = await response.json();
  if (parsedResponse.status !== 'OK') {
    throw new Error(parsedResponse.message || 'Failed to parse response from /api/agent/admin/block-content/:websiteId/pages/:pageId/blocks/:blockId');
  }

  return normalizeAdminBlockContentSummary(parsedResponse.blockContent);
};

export const pushAdminBlockContentToWebliumByBlockId = async (
  token: string,
  websiteId: string,
  pageId: string,
  blockId: string
): Promise<AdminBlockContentPushResponseType> => {
  const response = await fetch(
    `${BACKEND_URL}/api/agent/admin/block-content/${encodeURIComponent(websiteId)}/pages/${encodeURIComponent(pageId)}/blocks/${encodeURIComponent(blockId)}/push`,
    {
      method: 'POST',
      headers: {
        'X-Internal-Authorization': `Bearer ${token}`,
      }
    }
  );

  const parsedResponse = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(formatApiErrorMessage(parsedResponse, response.status, 'Failed to push block content to Weblium'));
  }

  if (
    parsedResponse.status !== 'OK'
    || typeof parsedResponse.message !== 'string'
    || typeof parsedResponse.websiteId !== 'string'
    || typeof parsedResponse.pageId !== 'string'
    || typeof parsedResponse.blockId !== 'string'
  ) {
    throw new Error('Failed to parse block content push response');
  }

  return parsedResponse as AdminBlockContentPushResponseType;
};

const getAdminTextElementUrl = (
  websiteId: string,
  pageId: string,
  blockId: string,
  elementId: string
): string => (
  `${BACKEND_URL}/api/agent/admin/block-content/${encodeURIComponent(websiteId)}`
  + `/pages/${encodeURIComponent(pageId)}/blocks/${encodeURIComponent(blockId)}`
  + `/elements/${encodeURIComponent(elementId)}`
);

const parseAdminTextElement = (value: unknown): AdminTextElementType => {
  if (!value || typeof value !== 'object') {
    throw new Error('Failed to parse text element response');
  }
  const element = value as AdminTextElementType;
  if (
    typeof element.websiteId !== 'string'
    || typeof element.pageId !== 'string'
    || typeof element.blockId !== 'string'
    || typeof element.elementId !== 'string'
    || typeof element.type !== 'string'
    || typeof element.content !== 'string'
  ) {
    throw new Error('Failed to parse text element response');
  }
  return element;
};

export const fetchAdminTextElementById = async (
  token: string,
  websiteId: string,
  pageId: string,
  blockId: string,
  elementId: string
): Promise<AdminTextElementType> => {
  const response = await fetch(getAdminTextElementUrl(websiteId, pageId, blockId, elementId), {
    headers: { 'X-Internal-Authorization': `Bearer ${token}` }
  });
  const parsedResponse = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(formatApiErrorMessage(parsedResponse, response.status, 'Failed to load text element'));
  }
  if (parsedResponse.status !== 'OK') {
    throw new Error(parsedResponse.message || 'Failed to load text element');
  }
  return parseAdminTextElement(parsedResponse.textElement);
};

export const saveAdminTextElementById = async (
  token: string,
  websiteId: string,
  pageId: string,
  blockId: string,
  elementId: string,
  content: string
): Promise<AdminTextElementType> => {
  const response = await fetch(getAdminTextElementUrl(websiteId, pageId, blockId, elementId), {
    method: 'PATCH',
    headers: {
      'X-Internal-Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ content })
  });
  const parsedResponse = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(formatApiErrorMessage(parsedResponse, response.status, 'Failed to save text element'));
  }
  if (parsedResponse.status !== 'OK') {
    throw new Error(parsedResponse.message || 'Failed to save text element');
  }
  return parseAdminTextElement(parsedResponse.textElement);
};

export const runTextElementQuickAction = async (
  token: string,
  websiteId: string,
  pageId: string,
  blockId: string,
  elementId: string,
  action: TextElementQuickActionType,
  text: string
): Promise<TextElementQuickActionResponseType> => {
  const response = await fetch(
    `${BACKEND_URL}/api/agent/quick-actions/websites/${encodeURIComponent(websiteId)}`
    + `/pages/${encodeURIComponent(pageId)}/blocks/${encodeURIComponent(blockId)}`
    + `/elements/${encodeURIComponent(elementId)}`,
    {
      method: 'POST',
      headers: {
        'X-Internal-Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ action, text })
    }
  );
  const parsedResponse = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(formatApiErrorMessage(parsedResponse, response.status, 'Text quick action failed'));
  }
  const quickAction = parsedResponse.quickAction as TextElementQuickActionResponseType | undefined;
  if (parsedResponse.status !== 'OK' || !quickAction || typeof quickAction.text !== 'string') {
    throw new Error(parsedResponse.message || 'Failed to parse text quick action response');
  }
  return quickAction;
};

export const fetchAdminBlockContentFlatElementsByBlockId = async (
  token: string,
  websiteId: string,
  pageId: string,
  blockId: string
): Promise<unknown> => {
  return fetchAdminBlockContentDataByBlockId(
    token,
    websiteId,
    pageId,
    blockId,
    '/flat-elements',
    'flatElements',
    'Failed to load block flat elements list'
  );
};

export const fetchAdminBlockContentTreeElementsByBlockId = async (
  token: string,
  websiteId: string,
  pageId: string,
  blockId: string
): Promise<unknown> => {
  return fetchAdminBlockContentDataByBlockId(
    token,
    websiteId,
    pageId,
    blockId,
    '/tree-elements',
    'treeElements',
    'Failed to load block tree elements list'
  );
};

export const fetchAdminBlockContentPropertiesByBlockId = async (
  token: string,
  websiteId: string,
  pageId: string,
  blockId: string
): Promise<unknown> => {
  return fetchAdminBlockContentDataByBlockId(
    token,
    websiteId,
    pageId,
    blockId,
    '/properties',
    'properties',
    'Failed to load block properties'
  );
};

export const fetchAdminBlockContentFullByBlockId = async (
  token: string,
  websiteId: string,
  pageId: string,
  blockId: string
): Promise<unknown> => {
  return fetchAdminBlockContentDataByBlockId(
    token,
    websiteId,
    pageId,
    blockId,
    '/full',
    'fullContent',
    'Failed to load full block content'
  );
};

export const fetchAdminBlockContentRawByBlockId = async (
  token: string,
  websiteId: string,
  pageId: string,
  blockId: string
): Promise<unknown> => {
  return fetchAdminBlockContentDataByBlockId(
    token,
    websiteId,
    pageId,
    blockId,
    '/raw',
    'rawContent',
    'Failed to load raw block content'
  );
};

// Sessions API
export const fetchSessions = async (token: string, type?: SessionType): Promise<SessionsListResponse['sessions']> => {
  const query = type ? `?type=${encodeURIComponent(type)}` : '';
  const response = await fetch(`${BACKEND_URL}/api/agent/sessions${query}`, {
    method: 'GET',
    headers: {
      'X-Internal-Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    }
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(formatApiErrorMessage(errorData, response.status));
  }

  const parsedResponse: SessionsListResponse = await response.json();
  if (parsedResponse.status !== 'OK') {
    throw new Error('Failed to fetch sessions list');
  }
  return parsedResponse.sessions;
};

export const createSession = async (
  token: string,
  name: string,
  allowedTools: string[] = [],
  language: string,
  optionsOrWebsiteId?: CreateSessionOptions | string | null,
  legacyPageName?: PageNameOption | '' | null
): Promise<CreateSessionResponse['session']> => {
  const options: CreateSessionOptions = typeof optionsOrWebsiteId === 'object' && optionsOrWebsiteId !== null
    ? optionsOrWebsiteId
    : {
      websiteId: optionsOrWebsiteId,
      pageName: legacyPageName
    };

  const sessionType = options.type || 'general';
  const normalizedWebsiteId = normalizeNullableString(options.websiteId);
  const normalizedPageName = normalizeNullableString(options.pageName);
  const requestPayload: Record<string, unknown> = {
    name,
    allowedTools,
    language,
    type: sessionType
  };

  if (normalizedWebsiteId !== null) {
    requestPayload.websiteId = normalizedWebsiteId;
  }

  if (normalizedPageName !== null) {
    requestPayload.pageName = normalizedPageName;
  }

  if (sessionType === 'vibe_block' && options.vibe_block) {
    requestPayload.vibe_block = options.vibe_block;
  }
  if (sessionType === 'vibe_site') {
    if (options.init_prompt) {
      requestPayload.init_prompt = options.init_prompt;
    }
    if (options.start_gathering !== undefined) {
      requestPayload.start_gathering = options.start_gathering;
    }
  }

  const response = await fetch(`${BACKEND_URL}/api/agent/sessions`, {
    method: 'POST',
    headers: {
      'X-Internal-Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestPayload)
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(formatApiErrorMessage(errorData, response.status));
  }

  const parsedResponse: CreateSessionResponse = await response.json();
  if (parsedResponse.status !== 'OK') {
    throw new Error('Failed to create session');
  }
  return parsedResponse.session;
};

export const deleteSessionById = async (token: string, sessionId: string): Promise<void> => {
  const response = await fetch(`${BACKEND_URL}/api/agent/sessions/${encodeURIComponent(sessionId)}`, {
    method: 'DELETE',
    headers: {
      'X-Internal-Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    }
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(formatApiErrorMessage(errorData, response.status));
  }
};

export const fetchSessionById = async (token: string, sessionId: string, language?: string): Promise<GetSessionResponse> => {
  const url = new URL(`${BACKEND_URL}/api/agent/sessions/${encodeURIComponent(sessionId)}`);
  if (language) {
    url.searchParams.set('language', language);
  }

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      'X-Internal-Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    }
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(formatApiErrorMessage(errorData, response.status));
  }

  const parsed: GetSessionResponse = await response.json();
  if (parsed.status !== 'OK') {
    throw new Error('Failed to fetch session');
  }
  return parsed;
};

export const setSessionContext = async (
  token: string,
  sessionId: string,
  { websiteId, pageName }: { websiteId?: string | null; pageName?: PageNameOption | '' | null }
): Promise<void> => {
  const normalizedWebsiteId = normalizeNullableString(websiteId);
  const normalizedPageName = normalizeNullableString(pageName);
  const response = await fetch(
    `${BACKEND_URL}/api/agent/sessions/${encodeURIComponent(sessionId)}/context`,
    {
      method: 'POST',
      headers: {
        'X-Internal-Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        websiteId: normalizedWebsiteId,
        pageName: normalizedPageName,
      }),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(formatApiErrorMessage(errorData, response.status, 'Failed to update session context'));
  }
};

const handleMessageActionResponse = async (response: Response, defaultError: string) => {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(formatApiErrorMessage(errorData, response.status, defaultError));
  }

  if (response.status === 204 || response.headers.get('Content-Length') === '0') {
    return undefined;
  }

  return response.json().catch(() => undefined);
};

export const updateMessageLikeStatus = async (
  token: string,
  sessionId: string,
  messageId: string,
  shouldLike: boolean
) => {
  const method = shouldLike ? 'POST' : 'DELETE';
  const response = await fetch(
    `${BACKEND_URL}/api/agent/sessions/${encodeURIComponent(sessionId)}/messages/${encodeURIComponent(messageId)}/like`,
    {
      method,
      headers: {
        'X-Internal-Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }
  );

  await handleMessageActionResponse(response, 'Failed to update like status');
};

export const updateMessageDislikeStatus = async (
  token: string,
  sessionId: string,
  messageId: string,
  shouldDislike: boolean
) => {
  const method = shouldDislike ? 'POST' : 'DELETE';
  const response = await fetch(
    `${BACKEND_URL}/api/agent/sessions/${encodeURIComponent(sessionId)}/messages/${encodeURIComponent(messageId)}/dislike`,
    {
      method,
      headers: {
        'X-Internal-Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }
  );

  await handleMessageActionResponse(response, 'Failed to update dislike status');
};

export const deleteMessageById = async (
  token: string,
  sessionId: string,
  messageId: string
) => {
  const response = await fetch(
    `${BACKEND_URL}/api/agent/sessions/${encodeURIComponent(sessionId)}/messages/${encodeURIComponent(messageId)}`,
    {
      method: 'DELETE',
      headers: {
        'X-Internal-Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }
  );

  await handleMessageActionResponse(response, 'Failed to delete message');
};

export const setMessageOfferPlanUpgradeStateById = async (
  token: string,
  sessionId: string,
  messageId: string,
  state: MessageOfferPlanUpgradeStateUpdateType
): Promise<MessageOfferPlanUpgradeActionResponse> => {
  const response = await fetch(
    `${BACKEND_URL}/api/agent/sessions/${encodeURIComponent(sessionId)}/messages/${encodeURIComponent(messageId)}/offer-plan-upgrade/state`,
    {
      method: 'POST',
      headers: {
        'X-Internal-Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ state })
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(formatApiErrorMessage(errorData, response.status, 'Failed to update message offer plan upgrade state'));
  }

  return await response.json() as MessageOfferPlanUpgradeActionResponse;
};

export const summarizeSession = async (
  token: string,
  sessionId: string
): Promise<SessionSummary> => {
  const response = await fetch(
    `${BACKEND_URL}/api/agent/sessions/${encodeURIComponent(sessionId)}/summarize`,
    {
      method: 'POST',
      headers: {
        'X-Internal-Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(formatApiErrorMessage(errorData, response.status, 'Failed to summarize session'));
  }

  const parsed = await response.json();
  if (parsed.status !== 'OK' || !parsed.summary) {
    throw new Error(parsed.message || 'Unexpected summarize response');
  }

  return parsed.summary as SessionSummary;
};

export const renameSessionByAI = async (
  token: string,
  sessionId: string,
  force: boolean = false
): Promise<{ renameOptions: SessionRenameOptions | null; sessionName: string }> => {
  const response = await fetch(
    `${BACKEND_URL}/api/agent/sessions/${encodeURIComponent(sessionId)}/rename`,
    {
      method: 'POST',
      headers: {
        'X-Internal-Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ force })
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(formatApiErrorMessage(errorData, response.status, 'Failed to rename session'));
  }

  const parsed = await response.json();
  if (parsed.status !== 'OK' || !parsed.sessionName) {
    throw new Error(parsed.message || 'Unexpected rename response');
  }

  return {
    renameOptions: parsed.renameOptions ? parsed.renameOptions as SessionRenameOptions : null,
    sessionName: parsed.sessionName
  };
};

export const fetchDecisionsBySessionId = async (
  token: string,
  sessionId: string
): Promise<DecisionListItemType[]> => {
  if (!sessionId) {
    return [];
  }

  const response = await fetch(`${BACKEND_URL}/api/agent/sessions/${encodeURIComponent(sessionId)}/decisions`, {
    method: 'GET',
    headers: {
      'X-Internal-Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(formatApiErrorMessage(errorData, response.status, 'Failed to fetch decisions'));
  }

  const parsedResponse: DecisionsListResponse = await response.json();
  if (parsedResponse.status !== 'OK') {
    throw new Error('Failed to fetch decisions');
  }

  return parsedResponse.decisions;
};

export const fetchDecisionGeneratedImageBlob = async (
  token: string,
  sessionId: string,
  decisionId: string
): Promise<Blob> => {
  const response = await fetch(
    `${BACKEND_URL}/api/agent/sessions/${encodeURIComponent(sessionId)}/decisions/${encodeURIComponent(decisionId)}/image`,
    {
      method: 'GET',
      headers: {
        'X-Internal-Authorization': `Bearer ${token}`
      }
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(formatApiErrorMessage(errorData, response.status, 'Failed to fetch generated image for decision'));
  }

  return await response.blob();
};

export const acceptDecisionById = async (
  token: string,
  sessionId: string,
  decisionId: string
): Promise<DecisionActionResponse> => {
  const response = await fetch(
    `${BACKEND_URL}/api/agent/sessions/${encodeURIComponent(sessionId)}/decisions/${encodeURIComponent(decisionId)}/accept`,
    {
      method: 'POST',
      headers: {
        'X-Internal-Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(formatApiErrorMessage(errorData, response.status, 'Failed to accept decision'));
  }

  return await response.json() as DecisionActionResponse;
};

export const declineDecisionById = async (
  token: string,
  sessionId: string,
  decisionId: string
): Promise<DecisionActionResponse> => {
  const response = await fetch(
    `${BACKEND_URL}/api/agent/sessions/${encodeURIComponent(sessionId)}/decisions/${encodeURIComponent(decisionId)}/decline`,
    {
      method: 'POST',
      headers: {
        'X-Internal-Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(formatApiErrorMessage(errorData, response.status, 'Failed to decline decision'));
  }

  return await response.json() as DecisionActionResponse;
};

export const setDecisionUserInputById = async (
  token: string,
  sessionId: string,
  decisionId: string,
  payload: {
    valueType: DecisionValueType;
    value_boolean?: boolean;
    value_number?: number;
    value_short_text?: string;
    value_long_text?: string;
  }
): Promise<DecisionActionResponse> => {
  const response = await fetch(
    `${BACKEND_URL}/api/agent/sessions/${encodeURIComponent(sessionId)}/decisions/${encodeURIComponent(decisionId)}/user-input`,
    {
      method: 'POST',
      headers: {
        'X-Internal-Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(formatApiErrorMessage(errorData, response.status, 'Failed to set user value for decision'));
  }

  return await response.json() as DecisionActionResponse;
};

export const setDecisionUpgradeRequiredStateById = async (
  token: string,
  sessionId: string,
  decisionId: string,
  state: DecisionUpgradeRequiredStateUpdateType
): Promise<DecisionActionResponse> => {
  const response = await fetch(
    `${BACKEND_URL}/api/agent/sessions/${encodeURIComponent(sessionId)}/decisions/${encodeURIComponent(decisionId)}/upgrade-required/state`,
    {
      method: 'POST',
      headers: {
        'X-Internal-Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ state })
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(formatApiErrorMessage(errorData, response.status, 'Failed to update decision upgradeRequired state'));
  }

  return await response.json() as DecisionActionResponse;
};

export const setDecisionWebsitePublishStateById = async (
  token: string,
  sessionId: string,
  decisionId: string,
  state: DecisionWebsitePublishStateUpdateType
): Promise<DecisionActionResponse> => {
  const response = await fetch(
    `${BACKEND_URL}/api/agent/sessions/${encodeURIComponent(sessionId)}/decisions/${encodeURIComponent(decisionId)}/website-publish-state`,
    {
      method: 'POST',
      headers: {
        'X-Internal-Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ website_publish_state: state })
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(formatApiErrorMessage(errorData, response.status, 'Failed to update decision website publish state'));
  }

  return await response.json() as DecisionActionResponse;
};

export class SiteBuilderRequestError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'SiteBuilderRequestError';
    this.status = status;
  }
}

export const isSiteBuilderConflictError = (error: unknown): error is SiteBuilderRequestError => (
  error instanceof SiteBuilderRequestError && error.status === 409
);

const siteBuilderRequest = async <T>(
  token: string,
  path: string,
  options: RequestInit = {}
): Promise<T> => {
  const response = await fetch(`${BACKEND_URL}/api/site-builder${path}`, {
    ...options,
    headers: {
      'X-Internal-Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(options.headers ?? {})
    }
  });
  const parsedResponse = await response.json().catch(() => ({}));
  if (!response.ok) {
    const baseMessage = formatApiErrorMessage(parsedResponse, response.status, 'Site brief request failed');
    const readiness = parsedResponse && typeof parsedResponse === 'object'
      ? (parsedResponse as { readiness?: SiteStructureReadinessType }).readiness
      : undefined;
    const readinessDetails = readiness
      ? [
        readiness.missing_requirement_keys.length > 0
          ? `Missing requirements: ${readiness.missing_requirement_keys.join(', ')}`
          : null,
        readiness.blocking_open_question_ids.length > 0
          ? `Blocking open questions: ${readiness.blocking_open_question_ids.join(', ')}`
          : null,
        ...readiness.unsupported_requirements.map(item => `${item.key}: ${item.reason}`)
      ].filter((item): item is string => item !== null)
      : [];
    throw new SiteBuilderRequestError(
      readinessDetails.length > 0
        ? `${baseMessage}\n${readinessDetails.join('\n')}`
        : baseMessage,
      response.status
    );
  }
  return parsedResponse as T;
};

export const fetchSiteBriefs = async (
  token: string,
  scope: SiteBriefListScopeType = 'own'
): Promise<SiteBriefListResponseType> => siteBuilderRequest<SiteBriefListResponseType>(
  token, `/site-briefs?scope=${encodeURIComponent(scope)}`
);

export const deleteSiteBriefs = async (
  token: string,
  brief?: { id: string; version: number }
): Promise<{ deleted_briefs: number; deleted_sessions: number; session_ids: string[] }> => {
  const result = await siteBuilderRequest<{ deleted_briefs: number; deleted_sessions: number; session_ids: string[] }>(
    token,
    brief ? `/site-briefs/${encodeURIComponent(brief.id)}` : '/site-briefs',
    { method: 'DELETE', ...(brief ? { body: JSON.stringify({ expected_version: brief.version }) } : {}) }
  );
  for (const sessionId of result.session_ids) {
    sessionStorage.removeItem(`site-brief-chat:${sessionId}`);
  }
  return result;
};

export type PendingSiteBriefChatRequest = Partial<SummaryAction> & {
  sessionId: string;
  message: string;
  expected_version: number;
  client_request_id: string;
};

export const getPendingSiteBriefChatRequest = (sessionId: string): PendingSiteBriefChatRequest | null => {
  try {
    const stored = sessionStorage.getItem(`site-brief-chat:${sessionId}`);
    const value = stored ? JSON.parse(stored) : null;
    if (value?.sessionId === sessionId && typeof value.message === 'string'
      && Number.isInteger(value.expected_version) && typeof value.client_request_id === 'string') {
      return value;
    }
  } catch {
    // A damaged browser cache must not prevent sending a message.
  }
  return null;
};

// Keep the request identity across uncertain network failures and page reloads.
export const sendSiteBriefChatRequest = async (
  token: string, sessionId: string, message: string, expectedVersion: number, messageId: string, action?: SummaryAction
): Promise<SiteBriefConversationReceipt> => {
  const storageKey = `site-brief-chat:${sessionId}`;
  const previous = getPendingSiteBriefChatRequest(sessionId);
  if (previous && (previous.message !== message || previous.summary_action !== action?.summary_action
    || previous.summary_before !== action?.summary_before)) {
    throw new Error('Retry the previous message before sending a different one. Its delivery is still being checked.');
  }
  const body = previous?.message === message
    ? previous
    : { sessionId, message, expected_version: expectedVersion, client_request_id: messageId, ...action };
  sessionStorage.setItem(storageKey, JSON.stringify(body));
  const socketResult = await socketCommand('site-brief:conversation', body);
  const response = socketResult ? new Response(JSON.stringify(socketResult.body), { status: socketResult.httpStatus }) : await fetch(`${BACKEND_URL}/api/site-builder/site-briefs/completion`, {
    method: 'POST',
    headers: { 'X-Internal-Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });
  if (!response.ok) {
    if (response.status < 500) sessionStorage.removeItem(storageKey);
    throw new SiteBuilderRequestError(formatApiErrorMessage(await response.json().catch(() => ({})), response.status), response.status);
  }
  const receipt = await response.json() as SiteBriefConversationReceipt;
  sessionStorage.removeItem(storageKey);
  return receipt;
};

export const fetchSessionSiteBriefTask = async (token: string, sessionId: string, taskId?: string): Promise<SiteBriefTaskResponse> => {
  const response = await fetch(`${BACKEND_URL}/api/agent/sessions/${encodeURIComponent(sessionId)}/site-brief-task${taskId ? `?taskId=${encodeURIComponent(taskId)}` : ''}`, {
    headers: { 'X-Internal-Authorization': `Bearer ${token}` }, cache: 'no-store'
  });
  if (!response.ok) throw new Error(formatApiErrorMessage(await response.json().catch(() => ({})), response.status));
  return response.json();
};

export const createSiteBriefChatSession = async (
  token: string, name: string, language: string, message: string,
  onSessionCreated?: (session: CreateSessionResponse['session'], messageId: string) => void
): Promise<{
  session: CreateSessionResponse['session'];
  receipt: SiteBriefConversationReceipt | null;
  submissionError: string | null;
}> => {
  const session = await createSession(token, name, [], language, { type: 'vibe_site' });
  const draftKey = `site-builder-draft:${session.id}`;
  const submittedKey = `site-builder-submitted:${session.id}`;
  sessionStorage.setItem(draftKey, message);
  sessionStorage.setItem(submittedKey, message);

  const messageId = crypto.randomUUID();
  const submission = session.site_brief?.status === 'gather_requirements'
    ? sendSiteBriefChatRequest(token, session.id, message, session.site_brief.version, messageId)
    : Promise.reject(new Error('The session was saved but is not ready for chat. Open Site briefs to start gathering.'));
  // Select the saved session while admission is in flight, so reloads and retries keep its identity.
  onSessionCreated?.(session, messageId);

  try {
    const receipt = await submission;
    sessionStorage.removeItem(draftKey);
    return { session, receipt, submissionError: null };
  } catch (failure) {
    // Creation succeeded. Open that session even if the first message needs a retry.
    if (!getPendingSiteBriefChatRequest(session.id)) {
      sessionStorage.removeItem(submittedKey);
    }
    return {
      session,
      receipt: null,
      submissionError: failure instanceof Error ? failure.message : 'Your session was saved. Please retry your message.',
    };
  }
};

export const fetchSiteBriefRequirementsDictionary = async (
  token: string
): Promise<{ requirements: SiteBriefRequirementDefinitionType[]; options: SiteBriefRegistryOptionsType }> => {
  const response = await siteBuilderRequest<{
    status: 'OK';
    requirements: SiteBriefRequirementDefinitionType[];
    options: SiteBriefRegistryOptionsType;
  }>(token, '/requirements-dictionary');
  return { requirements: response.requirements, options: response.options };
};

let siteBriefFontCatalogRequest: Promise<SiteBriefFontCatalogEntryType[]> | null = null;

// The catalog never changes while the page is open, so it is asked for once; a failed request is tried again next time.
export const fetchSiteBriefFontCatalog = (token: string): Promise<SiteBriefFontCatalogEntryType[]> => {
  if (!siteBriefFontCatalogRequest) {
    siteBriefFontCatalogRequest = siteBuilderRequest<{ status: 'OK'; fonts: SiteBriefFontCatalogEntryType[] }>(
      token,
      '/font-catalog'
    )
      .then((response) => response.fonts)
      .catch((error) => {
        siteBriefFontCatalogRequest = null;
        throw error;
      });
  }
  return siteBriefFontCatalogRequest;
};

export const fetchSiteBriefById = async (token: string, siteBriefId: string): Promise<SiteBriefType> => {
  const response = await siteBuilderRequest<{ status: 'OK'; site_brief: SiteBriefType }>(
    token,
    `/site-briefs/${encodeURIComponent(siteBriefId)}`
  );
  return response.site_brief;
};

export const fetchSiteBriefRequirementEvents = async (
  token: string,
  siteBriefId: string,
  requirementKey: string
): Promise<SiteBriefRequirementEventType[]> => {
  const response = await siteBuilderRequest<{
    status: 'OK';
    event_log: { events: SiteBriefRequirementEventType[] };
  }>(
    token,
    `/site-briefs/${encodeURIComponent(siteBriefId)}/requirement-events?key=${encodeURIComponent(requirementKey)}`
  );
  return response.event_log.events;
};

export const updateSiteBriefStatus = async (
  token: string,
  siteBriefId: string,
  expectedVersion: number,
  status: SiteBriefStatusType
): Promise<SiteBriefType> => {
  const response = await siteBuilderRequest<{ status: 'OK'; site_brief: SiteBriefType }>(
    token,
    `/site-briefs/${encodeURIComponent(siteBriefId)}/status`,
    {
      method: 'PATCH',
      body: JSON.stringify({ expected_version: expectedVersion, status })
    }
  );
  return response.site_brief;
};

export const suggestSiteBriefRequirements = async (
  token: string,
  siteBriefId: string,
  expectedVersion: number
): Promise<SiteBriefType> => {
  const response = await siteBuilderRequest<{ status: 'OK'; site_brief: SiteBriefType }>(
    token,
    `/site-briefs/${encodeURIComponent(siteBriefId)}/suggest-requirements`,
    {
      method: 'POST',
      body: JSON.stringify({ expected_version: expectedVersion })
    }
  );
  return response.site_brief;
};

export const fetchSiteBriefSiteStructure = async (
  token: string,
  siteBriefId: string
): Promise<SiteStructureType> => {
  const response = await siteBuilderRequest<{ status: 'OK'; site_structure: SiteStructureType }>(
    token,
    `/site-briefs/${encodeURIComponent(siteBriefId)}/site-structure`
  );
  return response.site_structure;
};

export const fetchSiteBriefRenderedSite = async (
  token: string,
  siteBriefId: string
): Promise<RenderedSiteType> => {
  const response = await siteBuilderRequest<{ status: 'OK'; rendered_site: RenderedSiteType }>(
    token,
    `/site-briefs/${encodeURIComponent(siteBriefId)}/rendered-site`
  );
  return response.rendered_site;
};

export const buildSiteBriefSiteStructure = async (
  token: string, siteBriefId: string, expectedVersion: number
): Promise<{ siteBrief: SiteBriefType }> => {
  const response = await siteBuilderRequest<{ status: 'OK'; site_brief: SiteBriefType }>(
    token, `/site-briefs/${encodeURIComponent(siteBriefId)}/build-site-structure`,
    { method: 'POST', body: JSON.stringify({ expected_version: expectedVersion }) }
  );
  return { siteBrief: response.site_brief };
};

export const generateSiteBriefCore = async (
  token: string, siteBriefId: string, expectedVersion: number
): Promise<{ siteBrief: SiteBriefType }> => {
  const response = await siteBuilderRequest<{ status: 'OK'; site_brief: SiteBriefType }>(
    token, `/site-briefs/${encodeURIComponent(siteBriefId)}/generate-site-core`,
    { method: 'POST', body: JSON.stringify({ expected_version: expectedVersion }) }
  );
  return { siteBrief: response.site_brief };
};

export const gatherSiteBriefRequirements = async (token: string, siteBriefId: string, expectedVersion: number): Promise<SiteBriefType> => {
  const response = await siteBuilderRequest<{ status: 'OK'; site_brief: SiteBriefType }>(token,
    `/site-briefs/${encodeURIComponent(siteBriefId)}/gather-requirements`,
    { method: 'POST', body: JSON.stringify({ expected_version: expectedVersion }) });
  return response.site_brief;
};

export const fulfillSiteBriefVibeBlocks = async (token: string, siteBriefId: string, expectedVersion: number) => {
  const response = await siteBuilderRequest<{ status: 'OK'; site_brief: SiteBriefType }>(token,
    `/site-briefs/${encodeURIComponent(siteBriefId)}/fulfill-vibe-blocks`,
    { method: 'POST', body: JSON.stringify({ expected_version: expectedVersion }) });
  return { siteBrief: response.site_brief };
};

export const appendSiteBriefRequirement = async (
  token: string,
  siteBriefId: string,
  expectedVersion: number,
  requirement: SiteBriefRequirementInputType
): Promise<{ siteBrief: SiteBriefType; requirementEvent: SiteBriefRequirementEventType }> => {
  const response = await siteBuilderRequest<{
    status: 'OK';
    site_brief: SiteBriefType;
    requirement_event: SiteBriefRequirementEventType;
  }>(
    token,
    `/site-briefs/${encodeURIComponent(siteBriefId)}/requirements`,
    {
      method: 'POST',
      body: JSON.stringify({ expected_version: expectedVersion, requirement })
    }
  );
  return { siteBrief: response.site_brief, requirementEvent: response.requirement_event };
};


/** Keep one request identity until its admission is known, including across reloads. */
export const sendSiteBriefBuildRequest = async (
  token: string, sessionId: string, briefId: string, expectedVersion: number, taskId?: string
): Promise<{ task: SiteBriefTaskType; site_brief: SiteBriefChatMetadata }> => {
  const storageKey = `site-builder-build-request:${sessionId}`;
  let previous: { briefId: string; body: { expected_version: number; client_request_id: string; task_id?: string } } | null = null;
  try {
    previous = JSON.parse(sessionStorage.getItem(storageKey) || 'null');
  } catch {
    sessionStorage.removeItem(storageKey);
  }
  const body = previous?.briefId === briefId && Number.isInteger(previous.body?.expected_version)
    && typeof previous.body?.client_request_id === 'string'
    ? previous.body
    : { expected_version: expectedVersion, client_request_id: crypto.randomUUID(), ...(taskId ? { task_id: taskId } : {}) };
  sessionStorage.setItem(storageKey, JSON.stringify({ briefId, body }));
  try {
    const result = await socketCommand('site-brief:build', { siteBriefId: briefId, ...body });
    if (result && result.httpStatus >= 400) throw new SiteBuilderRequestError(formatApiErrorMessage(result.body, result.httpStatus), result.httpStatus);
    const receipt = result ? result.body as { task: SiteBriefTaskType; site_brief: SiteBriefChatMetadata } : await siteBuilderRequest<{ task: SiteBriefTaskType; site_brief: SiteBriefChatMetadata }>(token,
      `/site-briefs/${encodeURIComponent(briefId)}/build-site`, { method: 'POST', body: JSON.stringify(body) });
    sessionStorage.removeItem(storageKey);
    return receipt;
  } catch (error) {
    if (error instanceof SiteBuilderRequestError && error.status < 500) sessionStorage.removeItem(storageKey);
    throw error;
  }
};

export const stopSiteBriefTask = async (token: string, briefId: string, taskId: string, expectedVersion: number): Promise<void> => {
  await siteBuilderRequest(token, `/site-briefs/${encodeURIComponent(briefId)}/tasks/${encodeURIComponent(taskId)}/stop`, {
    method: 'POST', body: JSON.stringify({ expected_version: expectedVersion }),
  });
};
